<template>
  <div>
    <h3 class="title title--6">
      Contact Info
    </h3>

    <text-input
      v-model="contactInfo.fullName"
      type="text"
      label="Full Name"
      name="fullName"
      icon-class="fa-user-circle"
      :validations="{
        full_name: true,
        required: true
      }"
    />

    <text-input
      v-if="emailIsShown"
      v-model="contactInfo.email"
      type="email"
      label="Email Address"
      name="email"
      :data-three-ds="paayEmailAttribute"
      :validations="{ required: emailIsRequired }"
      :show-as-optional="emailInputState === 'optional' && !requestBillingEmail"
    />

    <transition name="fade-down-only-in">
      <text-input
        v-if="phoneIsShown"
        v-model="contactInfo.phoneNumber"
        type="phone"
        label="Phone Number"
        name="phoneNumber"
        :validations="{
          required: true
        }"
      />
    </transition>

    <!--
      Hidden inputs the Paay 3DS SDK scrapes for the cardholder's mobile
      phone. Paay reads the country calling code (mobileCC, "1" for US) and
      the national number (mobilePhoneNum) from separate DOM elements
      (https://docs.3dsintegrator.com/docs/dom-elements).
    -->
    <template v-if="renderPaayMobileInputs">
      <text-input
        v-show="false"
        type="text"
        label="mobileCC"
        name="mobileCC"
        :model-value="billingMobileCallingCode"
        data-three-ds="mobileCC"
      />

      <text-input
        v-show="false"
        type="text"
        label="mobilePhoneNum"
        name="mobilePhoneNum"
        :model-value="billingMobileNationalNumber"
        data-three-ds="mobilePhoneNum"
      />
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'ContactInformation',

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      emailInputState: {
        type: String,
        default: 'required',
        validator(value) {
          return ['required', 'optional', 'hidden'].includes(value);
        }
      },
      phoneIsRequired: {
        type: Boolean,
        default: false
      },
      tdsProvider: {
        type: String,
        default: ''
      },
      requestBillingEmail: {
        type: Boolean,
        default: false
      },
      requestBillingPhone: {
        type: Boolean,
        default: false
      },
      modelValue: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        contactInfo: {
          email: null,
          fullName: null,
          phoneNumber: null
        }
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser', 'session']),

      emailIsShown() {
        return this.emailInputState !== 'hidden' || this.requestBillingEmail;
      },

      emailIsRequired() {
        return this.emailInputState === 'required' || this.requestBillingEmail;
      },

      phoneIsShown() {
        return this.phoneIsRequired || this.session.smsReceiptOptIn || this.requestBillingPhone;
      },

      paayEmailAttribute() {
        return this.tdsProvider === 'Paay' && this.requestBillingEmail ? 'email' : null;
      },

      renderPaayMobileInputs() {
        return this.tdsProvider === 'Paay' && this.requestBillingPhone;
      },

      billingMobileNationalNumber() {
        const digits = (this.contactInfo.phoneNumber || '').replace(/\D/g, '');

        if (!digits) return null;

        /* Legacy contact phone is US-only; strip a leading '1' country code. */
        return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
      },

      billingMobileCallingCode() {
        return this.billingMobileNationalNumber ? '1' : null;
      }
    },

    watch: {
      contactInfo: {
        deep: true,
        handler: 'updateContactInfo',
        immediate: true
      },
      'currentUser.accountId': {
        handler: 'signedInHandler',
        immediate: true
      }
    },

    methods: {
      signedInHandler() {
        if (this.currentUser.accountId) {
          this.contactInfo.email = this.currentUser.email;
          this.contactInfo.fullName = this.currentUser.fullName;
          this.contactInfo.phoneNumber = this.currentUser.primarySmsNumber;
        }
      },

      updateContactInfo() {
        this.$emit('update:modelValue', this.contactInfo);
      }
    }
  };
</script>
